<template>
  <public-page>
    <v-row align="center" justify="center" class="fill-height">
      <v-col cols="12" lg="6" md="8" sm="12">
        <v-card class="auth-card" elevation="8" rounded="lg" dark>
          <v-toolbar class="auth-toolbar" flat dark height="140">
            <v-img
              :src="require('@/assets/camceadhub-logo-dark.png')"
              contain
              max-height="116"
              max-width="340"
              class="mx-auto"
            ></v-img>
          </v-toolbar>

          <div v-if="!success">
            <v-card-text>
              <v-alert
                v-if="error"
                type="error"
                border="right"
                colored-border
                elevation="2"
              >
                {{ error }}
              </v-alert>
              <v-form
                lazy-validation
                v-model="valid"
                id="form"
                ref="form"
                action="POST"
                @submit.prevent="userSignUp"
              >
                <v-container class="text-center">
                  <v-row class="text-center">
                    <v-col>
                      <v-text-field
                        label="First Name"
                        :min="0"
                        :counter="25"
                        v-model="SignUpDetails.first_name"
                        :rules="nameRules"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        label="Last Name"
                        :min="0"
                        :counter="25"
                        v-model="SignUpDetails.last_name"
                        :rules="nameRules"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        label="Email"
                        :min="0"
                        :counter="50"
                        v-model="SignUpDetails.email"
                        :rules="emailRules"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        label="Username"
                        :min="0"
                        :counter="50"
                        v-model="SignUpDetails.username"
                        :rules="nameRules"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-text-field
                        label="CRSid (optional)"
                        hint="University of Cambridge Raven identifier - only needed if you want to log in with Raven/CAMCEAD SSO"
                        persistent-hint
                        v-model="SignUpDetails.crsid"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-text-field
                        label="Password"
                        hint="At least 8 characters"
                        :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                        :type="togglepass ? 'text' : 'password'"
                        :min="0"
                        :counter="25"
                        v-model="SignUpDetails.password1"
                        :rules="[required, min8, strength]"
                        @click:append="togglepass = !togglepass"
                      ></v-text-field>
                      Password strength:
                      <v-progress-linear
                        :color="score.color"
                        :value="score.value"
                      ></v-progress-linear>
                    </v-col>
                    <v-col>
                      <v-text-field
                        label="Password confirmation"
                        hint="At least 8 characters"
                        :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                        :type="togglepass ? 'text' : 'password'"
                        :min="0"
                        :counter="25"
                        v-model="SignUpDetails.password2"
                        :rules="[required, min8, matchingPasswords]"
                        @click:append="togglepass = !togglepass"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card-text>
            <v-divider class="auth-divider"></v-divider>
            <v-card-actions>
              <v-btn
                color="grey lighten-1"
                plain
                @click="$router.push({ name: 'SignIn' }, () => {})"
              >
                Already have an account?
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                type="submit"
                form="form"
                @click="validate"
                outlined
                color="grey lighten-1"
                :disabled="!valid"
              >
                <v-icon left>mdi-lock</v-icon>
                Register
              </v-btn>
            </v-card-actions>
          </div>
          <div v-else>
            <v-card-text>
              <p>
                Thank you for registering. Please check your email for a
                confirmation link to validate your account.
              </p>
            </v-card-text>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </public-page>
</template>
  
  <script>
import { API } from "@/axios-config";
import PublicPage from "@/components/PublicPage";
import zxcvbn from "zxcvbn";

export default {
  name: "RegistrationView",

  components: {
    PublicPage,
  },
  data() {
    return {
      SignUpDetails: {
        username: "",
        password1: "",
        password2: "",
        first_name: "",
        last_name: "",
        email: "",
        crsid: "",
      },
      nameRules: [
        (v) => !!v || "Name is required",
        (v) =>
          (v && v.length > 2) || "A name must be more than 2 characters long",
        (v) =>
          /^[A-Za-z0-9_]+$/.test(v) ||
          "A name can only contain letters and digits",
      ],
      usernameRules: [
        (v) => !!v || "Username is required",
        (v) =>
          (v && v.length > 5) ||
          "A username must be more than 2 characters long",
        (v) =>
          /^[a-z0-9_]+$/.test(v) ||
          "A username can only contain letters and digits",
      ],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) =>
          /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/.test(v) ||
          "Invalid Email address",
      ],
      passRules: [
        (v) => !!v || "Password is required",
        (v) =>
          (v && v.length > 7) ||
          "The password must be longer than 7 characters",
      ],
      togglepass: false,
      valid: true,
      error: null,
      success: false,
    };
  },
  computed: {
    score() {
      const result = zxcvbn(this.SignUpDetails.password1);

      switch (result.score) {
        case 4:
          return {
            color: "light-blue",
            value: 100,
          };
        case 3:
          return {
            color: "light-green",
            value: 75,
          };
        case 2:
          return {
            color: "yellow",
            value: 50,
          };
        case 1:
          return {
            color: "orange",
            value: 25,
          };
        default:
          return {
            color: "red",
            value: 0,
          };
      }
    },
  },
  methods: {
    required: function (value) {
      if (value) {
        return true;
      } else {
        return "This field is required.";
      }
    },
    min8: function (value) {
      if (!value) {
        return false;
      }
      if (value.length >= 8) {
        return true;
      } else {
        return "Password should have more than 8 characters.";
      }
    },
    min3: function (value) {
      if (!value) {
        return false;
      }
      if (value.length >= 3) {
        return true;
      } else {
        return "Password should have more than 3 characters.";
      }
    },
    matchingPasswords: function () {
      if (this.SignUpDetails.password1 === this.SignUpDetails.password2) {
        return true;
      } else {
        return "Passwords do not match.";
      }
    },
    strength: function (value) {
      const score = zxcvbn(value).score;
      if (score >= 3) {
        return true;
      } else {
        return "Password is too weak.";
      }
    },
    async userSignUp() {
      try {
        await API.post("/api/v1/accounts/register/", {
          username: this.SignUpDetails.username,
          password1: this.SignUpDetails.password1,
          password2: this.SignUpDetails.password2,
          email: this.SignUpDetails.email,
          first_name: this.SignUpDetails.first_name,
          last_name: this.SignUpDetails.last_name,
          crsid: this.SignUpDetails.crsid,
        });
        this.success = true;
      } catch (error) {
        if (error.response.status === 400) {
          if (error.response.data.username) {
            this.error = error.response.data.username[0];
          } else if (error.response.data.password1) {
            this.error = error.response.data.password1[0];
          } else if (error.response.data.password2) {
            this.error = error.response.data.password2[0];
          } else if (error.response.data.email) {
            this.error = error.response.data.email[0];
          } else if (error.response.data.first_name) {
            this.error = error.response.data.first_name[0];
          } else if (error.response.data.last_name) {
            this.error = error.response.data.last_name[0];
          } else if (error.response.data.crsid) {
            this.error = error.response.data.crsid[0];
          } else {
            this.error =
              "There is a problem connecting to the server. Please try again later.";
          }
        } else {
          this.error =
            "There is a problem connecting to the server. Please try again later.";
        }
      }
    },
    validate() {
      this.error = null;
      this.$refs.form.validate();
    },
  },
};
</script>
  
  <style scoped>
.v-btn {
  border-radius: 4px;
}

.auth-card.v-card {
  background-color: #101014 !important;
}

.auth-toolbar.v-toolbar {
  background-color: #101014 !important;
}

.auth-divider.v-divider {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.auth-card ::v-deep input:-webkit-autofill,
.auth-card ::v-deep input:-webkit-autofill:hover,
.auth-card ::v-deep input:-webkit-autofill:focus,
.auth-card ::v-deep input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #101014 inset !important;
  -webkit-text-fill-color: #fff !important;
  caret-color: #fff !important;
  transition: background-color 9999s ease-in-out 0s;
}
</style>